import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import ClientStorage from '@/utils/clientStorage';

const getRootCategory = (path: string) => {
  if (path) {
    return path.split('/')[0];
  }
  return null;
};

const loadFilterFromStorage = (brandId: string, path: string) => {
  const root = ClientStorage.getValue(brandId, true);

  if (root?.facets) {
    const categoryId = getRootCategory(path);
    return categoryId && root.facets[categoryId];
  }

  return null;
};

const updateFilterInStorage = (brandId: string, path: string, value: any[] | null) => {
  try {
    const root = ClientStorage.getValue(brandId, true) || {};

    if (!Object.prototype.hasOwnProperty.call(root, 'facets')) {
      root.facets = {};
    }

    const categoryId = getRootCategory(path);

    if (categoryId) {
      if (Array.isArray(value) && value.length > 0) {
        root.facets[categoryId] = value;
      } else if (Object.prototype.hasOwnProperty.call(root.facets, categoryId)) {
        delete root.facets[categoryId];
      }
    }

    ClientStorage.setValue(brandId, root, true);
  } catch (e) {
    console.error(e);
    ClientStorage.removeValue(brandId, true);
    return false;
  }

  return true;
};

interface Filter {
  id: string;
  value?: any;
  name?: string;
  minValue?: string;
  maxValue?: string;
}

interface Facet {
  id: string;
  name?: string;
  unit?: string;
}

interface FilterArgs {
  brandId: string;
  path: string;
  value: Filter;
}

export const useProductStore = defineStore('useProductStore', () => {
  const activeFilters = ref<Filter[]>([]);
  const isFilterSet = ref(false);
  const activeFacets = ref<Facet[]>([]);
  const isFiltersFixed = ref(false);

  const apiFilters = computed(() =>
    activeFilters.value.map((filter) => {
      const activeFacet = activeFacets.value.find((facet) => facet.id === filter.id);
      const unit = activeFacet ? activeFacet.unit : undefined;
      if (filter.minValue && filter.maxValue) {
        return {
          id: filter.id,
          value: null,
          minValue: Number.parseInt(filter.minValue, 10),
          maxValue: Number.parseInt(filter.maxValue, 10),
          unit,
        };
      }
      return {
        id: filter.id,
        value: filter.value,
        minValue: null,
        maxValue: null,
        unit,
      };
    }),
  );

  const visibleFilters = computed(() => {
    const result: Filter[] = [];

    activeFilters.value.forEach((activeFilter) => {
      const activeFacet = activeFacets.value.find((o) => o.id === activeFilter.id);
      let filter = activeFilter;
      if (!activeFacet) {
        const wildCardFacet = /(.*)\*$/.exec(filter.id);
        if (!wildCardFacet) {
          return;
        }
        const [, idStart] = wildCardFacet;
        const actualActiveFacet = activeFacets.value.find((o) => o.id.startsWith(idStart));
        if (!actualActiveFacet) {
          return;
        }
        if (activeFilters.value.some((o) => o.id === actualActiveFacet.id)) {
          return;
        }
        filter = {
          ...filter,
          id: actualActiveFacet.id,
          name: actualActiveFacet.name,
        };
      }
      if (filter.name) {
        result.push(filter);
      } else if (activeFacet) {
        result.push({
          ...filter,
          name: activeFacet.name,
        });
      } else {
        console.error('got neither filter nor active facet');
      }
    });

    return result;
  });

  const filterSet = () => {
    isFilterSet.value = true;
  };

  const addFilter = (args: FilterArgs) => {
    const filters = activeFilters.value.filter((filter) => filter.id !== args.value.id);
    filters.push(args.value);

    if (updateFilterInStorage(args.brandId, args.path, filters)) {
      activeFilters.value = filters;
    }
  };

  const removeFilter = (args: FilterArgs) => {
    const filters = activeFilters.value.filter((filter) => {
      if (filter.id === args.value.id) {
        return false;
      }
      const wildCardFacet = /(.*)\*$/.exec(filter.id);
      if (wildCardFacet) {
        const [, idStart] = wildCardFacet;
        return !args.value.id.startsWith(idStart);
      }
      return true;
    });

    if (updateFilterInStorage(args.brandId, args.path, filters)) {
      activeFilters.value = filters;
    }
  };

  const clearFilter = (args: { brandId: string; path: string }) => {
    if (updateFilterInStorage(args.brandId, args.path, null)) {
      activeFilters.value = [];
    }
  };

  const resetFilters = (args: { brandId: string; path: string }) => {
    clearFilter(args);
  };

  const loadFilter = (args: { brandId: string; path: string }) => {
    try {
      const filters = loadFilterFromStorage(args.brandId, args.path);
      if (filters) {
        activeFilters.value = filters;
      } else {
        resetFilters(args);
      }
    } catch (e) {
      console.error(e);
      discardFilters(args);
    }
  };

  const discardFilters = (args: { brandId: string }) => {
    ClientStorage.removeValue(args.brandId, true);
    activeFilters.value = [];
  };

  const setActiveFacets = (facets: Facet[]) => {
    activeFacets.value = facets;
  };

  const setIsFiltersFixed = (value: boolean) => {
    isFiltersFixed.value = value;
  };

  return {
    activeFilters,
    isFilterSet,
    activeFacets,
    isFiltersFixed,
    apiFilters,
    visibleFilters,
    filterSet,
    addFilter,
    removeFilter,
    clearFilter,
    resetFilters,
    loadFilter,
    discardFilters,
    setActiveFacets,
    setIsFiltersFixed,
  };
});
